/**
 * Starting or joining a game of hartenjagen.
 *
 * Shorter than the boerenbridge equivalent by design: there is nothing to
 * configure. Boerenbridge has house rules because every family plays it
 * differently; hartenjagen has one set of rules, and inventing settings for it
 * would be inventing work.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Panel, Screen, Segmented } from '@/components/ui';
import WhoAreYou, { type Chosen } from '@/components/WhoAreYou';
import { api, ApiError } from '@/lib/api';
import { ROOM_CODE_LENGTH, isValidRoomCode, normalizeRoomCode } from '@/live/roomCode';
import { seatFor } from '@/live/sessions';
import { useRules } from '@/store/rules';

export default function JoinHearts() {
  const navigate = useNavigate();
  const params = useParams<{ code?: string }>();

  const [code, setCode] = useState(params.code ? normalizeRoomCode(params.code) : '');
  const [me, setMe] = useState<Chosen>(() => ({
    name: seatFor(params.code ?? '')?.name ?? '',
    profileId: null,
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * Two games go by this name, and which one the table plays changes the deck,
   * the deal and every number on the screen. Remembered like the other house
   * rules, so nobody has to pick it twice.
   */
  const variant = useRules((state) => state.heartsVariant);
  const rememberHearts = useRules((state) => state.rememberHeartsVariant);

  const codeOk = isValidRoomCode(code);
  const named = me.name.trim().length > 0;

  async function createRoom() {
    if (!named) {
      setError('Kies eerst wie je bent.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { code: created } = await api<{ code: string }>('/api/hearts/rooms', {
        method: 'POST',
        body: JSON.stringify({ variant }),
      });
      rememberHearts(variant);
      navigate(`/harten/${created}`, { state: { name: me.name.trim(), profileId: me.profileId } });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kon geen potje aanmaken.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen title="Hartenjagen" back="/" subtitle="Met z'n vieren, laagste score wint">
      {error && (
        <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
          {error}
        </p>
      )}

      <Panel title="Wie ben je">
        <WhoAreYou value={me} onChange={setMe} />
      </Panel>

      <Panel title="Welk hartenjagen">
        <Segmented
          value={variant}
          onChange={rememberHearts}
          options={[
            {
              value: 'nederlands',
              label: 'Nederlands',
              hint: '32 kaarten · harten 1, ♠V 5, ♣B 2 · 15 per ronde · niets doorgeven',
            },
            {
              value: 'amerikaans',
              label: 'Amerikaans',
              hint: '52 kaarten · harten 1, ♠V 13 · 26 per ronde · drie kaarten doorgeven',
            },
          ]}
        />
        <p className="mt-3 text-sm text-slate-500">
          Dit geldt voor het potje dat je zelf aanmaakt. Doe je mee met een code, dan speel je wat
          die tafel heeft gekozen.
        </p>
      </Panel>

      <Panel title="Meedoen met een potje">
        <input
          value={code}
          onChange={(event) =>
            setCode(normalizeRoomCode(event.target.value).slice(0, ROOM_CODE_LENGTH))
          }
          placeholder="Code"
          inputMode="text"
          autoCapitalize="characters"
          autoComplete="off"
          className="min-h-14 w-full rounded-xl border border-white/10 bg-base-900 px-3 text-center font-tabular text-2xl tracking-[0.4em] outline-none focus:border-accent-500"
        />
        <div className="mt-3">
          <Button
            variant="primary"
            full
            disabled={!codeOk || !named}
            onClick={() =>
              navigate(`/harten/${normalizeRoomCode(code)}`, {
                state: { name: me.name.trim(), profileId: me.profileId },
              })
            }
          >
            Meedoen
          </Button>
        </div>
      </Panel>

      <Panel title="Of begin er zelf een">
        <Button variant="primary" full disabled={busy || !named} onClick={() => void createRoom()}>
          Nieuw potje hartenjagen
        </Button>
        <p className="mt-2 text-sm text-slate-500">
          Je speelt tot iemand 100 punten heeft. Harten tellen één, de schoppenvrouw dertien — en
          wie ze alle 26 pakt, schiet de maan en krijgt zelf niets.
        </p>
      </Panel>
    </Screen>
  );
}
